import { MutableRefObject, useEffect, useState, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import {
  $convertToMarkdownString,
} from '@lexical/markdown';
import { ListNode, ListItemNode } from '@lexical/list';
import { HeadingNode } from '@lexical/rich-text';
import { EditorState, LineBreakNode } from 'lexical';
import EditorToolbar from '@/components/Editor/EditorToolbar/EditorToolbar.tsx';
import styles from './Editor.module.scss';
import EditorCopyButtonsPlugin from '@/components/Editor/EditorCopyButtonsPlugin';
import style from '@/modules/Note/Note.module.scss';
import ProviderNoteTemplates from '@/components/ProviderNoteTemplates/ProviderNoteTemplates.tsx';
import React from 'react';
import {
  addReevaluationStarted,
  selectAddNewModalOpen,
  updateNoteTemplateRequested,
} from '@ax/redux-store';
import { useAppSelector } from '@ax/redux-store';
import { selectProviderNoteTemplates } from '@ax/redux-store';
import { selectNoteTemplateIdSelected } from '@ax/redux-store';
import { useAppDispatch } from '@ax/redux-store';
import { hasRecordingsSelector } from '@ax/redux-store';
import { selectIsNoteTemplatesEnabled } from '@ax/redux-store';
import { Button } from '@ax/ui';
import { AddReEvaluationModal } from '@/components/AddReEvaluationModal';
import { selectIsEdEnabled } from '@ax/redux-store';
import { selectIsMDMEnabled } from '@ax/redux-store';
import MarkdownPlugin from './MarkdownPlugin';

interface EditorProps {
  content: string;
  isEditable: boolean;
  onContentChange: (content: string) => void;
  onEditorBlur: () => void;
  namespace: string;
}

function onError(error: Error): void {
  console.error(error);
}

export default function Editor({
  content,
  isEditable,
  onContentChange,
  onEditorBlur,
  namespace,
}: Readonly<EditorProps>) {
  const dispatch = useAppDispatch();
  const [selectedProviderNoteTemplate, setSelectedProviderNoteTemplate] = useState<{
    templateId: string;
    templateName: string;
  }>();
  const [currentContent, setCurrentContent] = useState(content);
  const componentRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const providerNoteTemplates = useAppSelector(selectProviderNoteTemplates);
  const visitsState = useAppSelector((state) => state.selectedVisit);
  const isNoteTemplatesEnabled = useAppSelector(selectIsNoteTemplatesEnabled);
  const selectedProviderNoteTemplateId = useAppSelector(selectNoteTemplateIdSelected);
  const isEdEnabled = useAppSelector(selectIsEdEnabled);
  const isMDMEnabled = useAppSelector(selectIsMDMEnabled);
  const isAddNewModalOpen = useAppSelector(selectAddNewModalOpen);

  const noteId = visitsState.uuid;
  const noteHasRecordings = useAppSelector(hasRecordingsSelector(noteId));

  useEffect(() => {
    setSelectedProviderNoteTemplate(
      providerNoteTemplates?.find(
        (template: { templateId: string; templateName: string }) =>
          template?.templateId === selectedProviderNoteTemplateId,
      ),
    );
  }, [providerNoteTemplates, selectedProviderNoteTemplateId]);

  const initialConfig = {
    theme: {},
    onError,
    editable: true,
    nodes: [HeadingNode, ListItemNode, ListNode, LineBreakNode],
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (componentRef.current && !componentRef.current.contains(event.relatedTarget)) {
      onEditorBlur();
    }
  };

  useEffect(() => {
    setCurrentContent(content);
  }, [content]);

  const handleEditorChange = (editorState: EditorState) => {
    const markdownContent = editorState.read(() => $convertToMarkdownString());
    onContentChange(markdownContent);
  };

  const onProviderNoteTemplateChange = async (newValue: { value: string; label: string }) => {
    setSelectedProviderNoteTemplate({
      templateId: newValue.value.toString(),
      templateName: newValue.label,
    });

    dispatch(
      updateNoteTemplateRequested({
        noteId: visitsState.uuid,
        templateId: newValue.value,
        regenerateNote: noteHasRecordings,
      }),
    );
  };

  return (
    <div
      ref={componentRef}
      onBlur={handleBlur}
      className={styles.editor}
    >
      <LexicalComposer
        initialConfig={{
          namespace: namespace,
          ...initialConfig,
        }}
      >
        <EditorCopyButtonsPlugin />
        {visitsState.uuid && isNoteTemplatesEnabled && (
          <div className={style.provider__note__template_container}>
            <ProviderNoteTemplates
              templates={providerNoteTemplates}
              selectedTemplate={
                selectedProviderNoteTemplate
                  ? selectedProviderNoteTemplate
                  : providerNoteTemplates
                  ? providerNoteTemplates[0]
                  : null
              }
              onValueChanged={(newValue: { value: string; label: string }) =>
                onProviderNoteTemplateChange(newValue)
              }
            />
          </div>
        )}
        <div style={{ display: 'flex' }}>
          <EditorToolbar isEditable={isEditable} />
          <AddReEvaluationModal
            modalOpen={isAddNewModalOpen}
          />
          {
            <div className={styles.editor__bar__container}>
              {isEdEnabled && isMDMEnabled && (
                <Button
                  onClick={() => {
                    dispatch(addReevaluationStarted({ noteId: noteId }));
                  }}
                >
                  Add Re-eval
                </Button>
              )}
            </div>
          }
        </div>
        <MarkdownPlugin
          content={currentContent}
          isEditable={isEditable}
        />
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className="editor-input"
              style={{
                padding: '10px',
                minHeight: '200px',
              }}
            />
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <OnChangePlugin onChange={handleEditorChange} />
        <HistoryPlugin />
      </LexicalComposer>
    </div>
  );
}
